import { useState, useEffect, useCallback } from 'react';
import { DataTable, Column } from '../components/DataTable';
import { sharepointService, SharePointConfig, SharePointMapping, SharePointMappingCreatePayload, SharePointMappingUpdatePayload } from '../services/sharepoint';
import { companyService, Company } from '../services/companies';
import { ApiError } from '../services/api';

type MappingViewMode = 'list' | 'create' | 'edit';

export function SharePointConfigPage() {
  // Connector config state
  const [config, setConfig] = useState<SharePointConfig>({
    tenant_id: '',
    client_id: '',
    client_secret: '',
    site_url: '',
  });
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSuccess, setConfigSuccess] = useState<string | null>(null);

  // Mappings state
  const [mappings, setMappings] = useState<SharePointMapping[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [mappingsLoading, setMappingsLoading] = useState(true);
  const [mappingViewMode, setMappingViewMode] = useState<MappingViewMode>('list');
  const [selectedMapping, setSelectedMapping] = useState<SharePointMapping | null>(null);
  const [mappingError, setMappingError] = useState<string | null>(null);
  const [mappingSuccess, setMappingSuccess] = useState<string | null>(null);
  const [mappingSubmitting, setMappingSubmitting] = useState(false);

  // Mapping form state
  const [mappingForm, setMappingForm] = useState<SharePointMappingCreatePayload>({
    company_id: '',
    sharepoint_site_url: '',
    folder_path: '',
  });

  const fetchConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const data = await sharepointService.getConfig();
      setConfig(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // No config saved yet — use defaults
      } else {
        setConfigError(err instanceof Error ? err.message : 'Failed to load configuration');
      }
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const fetchMappings = useCallback(async () => {
    try {
      setMappingsLoading(true);
      const [mappingsData, companiesData] = await Promise.all([
        sharepointService.listMappings(),
        companyService.list(),
      ]);
      setMappings(mappingsData);
      setCompanies(companiesData);
    } catch (err) {
      setMappingError(err instanceof Error ? err.message : 'Failed to load mappings');
    } finally {
      setMappingsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchMappings();
  }, [fetchConfig, fetchMappings]);

  function showConfigSuccess(message: string) {
    setConfigSuccess(message);
    setTimeout(() => setConfigSuccess(null), 4000);
  }

  function showMappingSuccess(message: string) {
    setMappingSuccess(message);
    setTimeout(() => setMappingSuccess(null), 4000);
  }

  function getErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 422) {
        const details = err.details as { fields?: Record<string, string> } | undefined;
        if (details?.fields) {
          return Object.values(details.fields).join('. ');
        }
        return err.message;
      }
      return err.message;
    }
    return err instanceof Error ? err.message : 'An unexpected error occurred';
  }

  // --- Connector Config Handlers ---

  async function handleSaveConfig(e: React.FormEvent) {
    e.preventDefault();
    setConfigError(null);
    setConfigSaving(true);
    try {
      const updated = await sharepointService.updateConfig(config);
      setConfig(updated);
      showConfigSuccess('SharePoint configuration saved successfully');
    } catch (err) {
      setConfigError(getErrorMessage(err));
    } finally {
      setConfigSaving(false);
    }
  }

  // --- Mapping CRUD Handlers ---

  function handleCreateMapping() {
    setMappingForm({ company_id: '', sharepoint_site_url: '', folder_path: '' });
    setMappingViewMode('create');
    setMappingError(null);
  }

  function handleEditMapping(mapping: SharePointMapping) {
    setSelectedMapping(mapping);
    setMappingForm({
      company_id: mapping.company_id,
      sharepoint_site_url: mapping.sharepoint_site_url,
      folder_path: mapping.folder_path,
    });
    setMappingViewMode('edit');
    setMappingError(null);
  }

  async function handleDeleteMapping(mapping: SharePointMapping) {
    const confirmed = window.confirm(
      `Are you sure you want to delete the mapping for folder "${mapping.folder_path}"? This action cannot be undone.`,
    );
    if (!confirmed) return;

    setMappingError(null);
    try {
      await sharepointService.deleteMapping(mapping.id);
      showMappingSuccess('Mapping deleted successfully');
      await fetchMappings();
    } catch (err) {
      setMappingError(getErrorMessage(err));
    }
  }

  function handleCancelMapping() {
    setMappingViewMode('list');
    setSelectedMapping(null);
    setMappingError(null);
  }

  async function handleSubmitMapping(e: React.FormEvent) {
    e.preventDefault();
    setMappingError(null);
    setMappingSubmitting(true);

    try {
      if (mappingViewMode === 'create') {
        await sharepointService.createMapping(mappingForm as SharePointMappingCreatePayload);
        showMappingSuccess('Mapping created successfully');
      } else if (mappingViewMode === 'edit' && selectedMapping) {
        const payload: SharePointMappingUpdatePayload = {
          company_id: mappingForm.company_id,
          sharepoint_site_url: mappingForm.sharepoint_site_url,
          folder_path: mappingForm.folder_path,
        };
        await sharepointService.updateMapping(selectedMapping.id, payload);
        showMappingSuccess('Mapping updated successfully');
      }
      setMappingViewMode('list');
      setSelectedMapping(null);
      await fetchMappings();
    } catch (err) {
      setMappingError(getErrorMessage(err));
    } finally {
      setMappingSubmitting(false);
    }
  }

  // --- Render ---

  const mappingColumns: Column<SharePointMapping>[] = [
    {
      key: 'company_name',
      header: 'Company',
      render: (m) => {
        const company = companies.find((c) => c.id === m.company_id);
        return company?.usual_name || m.company_name || '—';
      },
    },
    { key: 'sharepoint_site_url', header: 'Site URL' },
    { key: 'folder_path', header: 'Folder Path' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>SharePoint Configuration</h1>
        <p>Configure SharePoint connector and folder mappings.</p>
      </div>

      {/* Connector Configuration Section */}
      <section className="config-section" aria-labelledby="connector-heading">
        <h2 id="connector-heading">Connector Settings</h2>

        {configError && <div className="alert alert-error" role="alert">{configError}</div>}
        {configSuccess && <div className="alert alert-success" role="status">{configSuccess}</div>}

        {configLoading ? (
          <div className="data-table__loading" role="status" aria-label="Loading configuration">
            <div className="data-table__spinner" />
            <span>Loading configuration...</span>
          </div>
        ) : (
          <form onSubmit={handleSaveConfig} className="form">
            <div className="form__field">
              <label htmlFor="tenant-id">Tenant ID</label>
              <input
                id="tenant-id"
                type="text"
                value={config.tenant_id}
                onChange={(e) => setConfig({ ...config, tenant_id: e.target.value })}
                placeholder="Enter Azure AD Tenant ID"
                required
              />
            </div>

            <div className="form__field">
              <label htmlFor="client-id">Client ID</label>
              <input
                id="client-id"
                type="text"
                value={config.client_id}
                onChange={(e) => setConfig({ ...config, client_id: e.target.value })}
                placeholder="Enter Application (Client) ID"
                required
              />
            </div>

            <div className="form__field">
              <label htmlFor="client-secret">Client Secret</label>
              <input
                id="client-secret"
                type="password"
                value={config.client_secret}
                onChange={(e) => setConfig({ ...config, client_secret: e.target.value })}
                placeholder="Enter Client Secret"
                required
              />
            </div>

            <div className="form__field">
              <label htmlFor="site-url">Site URL</label>
              <input
                id="site-url"
                type="url"
                value={config.site_url}
                onChange={(e) => setConfig({ ...config, site_url: e.target.value })}
                placeholder="https://yourcompany.sharepoint.com/sites/yoursite"
                required
              />
            </div>

            <div className="form__actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={configSaving}
              >
                {configSaving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </form>
        )}
      </section>

      {/* Folder-to-Company Mappings Section */}
      <section className="config-section" aria-labelledby="mappings-heading">
        <div className="page-header__row">
          <h2 id="mappings-heading">Folder-to-Company Mappings</h2>
          {mappingViewMode === 'list' && (
            <button
              className="btn btn-primary"
              onClick={handleCreateMapping}
            >
              Add Mapping
            </button>
          )}
        </div>

        {mappingError && <div className="alert alert-error" role="alert">{mappingError}</div>}
        {mappingSuccess && <div className="alert alert-success" role="status">{mappingSuccess}</div>}

        {mappingViewMode !== 'list' ? (
          <form onSubmit={handleSubmitMapping} className="form">
            <h3>{mappingViewMode === 'create' ? 'Create Mapping' : 'Edit Mapping'}</h3>

            <div className="form__field">
              <label htmlFor="mapping-company">Company</label>
              <select
                id="mapping-company"
                value={mappingForm.company_id}
                onChange={(e) => setMappingForm({ ...mappingForm, company_id: e.target.value })}
                required
              >
                <option value="">Select a company</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.usual_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form__field">
              <label htmlFor="mapping-site-url">SharePoint Site URL</label>
              <input
                id="mapping-site-url"
                type="url"
                value={mappingForm.sharepoint_site_url}
                onChange={(e) => setMappingForm({ ...mappingForm, sharepoint_site_url: e.target.value })}
                placeholder="https://yourcompany.sharepoint.com/sites/yoursite"
                required
              />
            </div>

            <div className="form__field">
              <label htmlFor="mapping-folder-path">Folder Path</label>
              <input
                id="mapping-folder-path"
                type="text"
                value={mappingForm.folder_path}
                onChange={(e) => setMappingForm({ ...mappingForm, folder_path: e.target.value })}
                placeholder="/Shared Documents/ClientFiles"
                required
              />
            </div>

            <div className="form__actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={mappingSubmitting}
              >
                {mappingSubmitting ? 'Saving...' : mappingViewMode === 'create' ? 'Create Mapping' : 'Update Mapping'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelMapping}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <DataTable<SharePointMapping>
            columns={mappingColumns}
            data={mappings}
            keyField="id"
            loading={mappingsLoading}
            emptyMessage="No folder mappings configured yet."
            onEdit={handleEditMapping}
            onDelete={handleDeleteMapping}
          />
        )}
      </section>
    </div>
  );
}
